import type { DsdEditorReviewMode } from "./rail/rail-counts";
import { cn } from "@/lib/utils";
import { NUMBER_BADGE_COUNT_MAX, NumberBadge } from "@/toggle-ui-kit/badge";
import {
  ChevronDown,
  CircleCheck,
  Eye,
  Funnel,
  ShieldAlert,
  Sigma,
  SquareDashedMousePointer,
  Type,
  type LucideIcon,
} from "lucide-react";
import { DSD_REVIEW_COPY } from "./copy";
import { DsdReviewHowToIcon } from "./how-to-icon";
import { useState } from "react";

const STEP_ICONS: Record<
  DsdEditorReviewMode,
  [LucideIcon, LucideIcon, LucideIcon]
> = {
  "note-reference-review": [SquareDashedMousePointer, Eye, Eye],
  "note-note-matching": [SquareDashedMousePointer, Eye, Eye],
  "sum-validation": [Sigma, SquareDashedMousePointer, Eye],
  corrections: [Type, Funnel, CircleCheck],
  "company-name-review": [Eye, SquareDashedMousePointer, ShieldAlert],
};

/** rail 옆에 떠 있는 카드. 항상 펼침, 여닫는 버튼 없음 */
type FloatingPlacement = { variant: "floating" };

/**
 * 패널 안. 머리글을 눌러 안내 영역을 여닫는다.
 * collapsed 가 바뀔 때마다 그 상태로 다시 시작한다 — 셀(항목)을 고르면 접힌 채, 풀면 펼친 채.
 * 그 사이엔 사용자가 여닫은 대로 둔다
 */
type EmbeddedPlacement = { variant: "embedded"; collapsed: boolean };

type GuideCardProps = {
  mode: DsdEditorReviewMode;
  count: number;
  className?: string;
} & (FloatingPlacement | EmbeddedPlacement);

const isCollapsed = (props: GuideCardProps) =>
  props.variant === "embedded" && props.collapsed;

/** 펼침 상태는 collapsed 가 바뀔 때만 초기화한다 (key 로 다시 마운트) */
export const DsdReviewGuideCard = (props: GuideCardProps) => (
  <GuideCard key={isCollapsed(props) ? "collapsed" : "open"} {...props} />
);

const GuideCard = (props: GuideCardProps) => {
  const { mode, count, variant, className } = props;
  const collapsible = variant === "embedded";
  const guide = DSD_REVIEW_COPY.guide[mode];
  const icons = STEP_ICONS[mode];
  const [open, setOpen] = useState(!isCollapsed(props));
  const header = (
    <>
      <DsdReviewHowToIcon />
      {/* 그라데이션은 디자인처럼 300px 폭(꺽쇠 왼쪽까지)에 걸고 글자는 그 왼쪽 일부만 보이게 — 글자 폭에 걸면 너무 가파르다 */}
      <span className="label-2-semibold bg-gradient-to-r from-primary-color to-pink-400 bg-clip-text bg-no-repeat text-transparent [background-size:300px_100%]">
        {DSD_REVIEW_COPY.guide.howTo}
      </span>
    </>
  );

  return (
    <section
      aria-label={DSD_REVIEW_COPY.rail[mode]}
      className={cn(
        "flex flex-col gap-4",
        variant === "floating" &&
          "w-[352px] rounded-2xl bg-background-base p-4 shadow-xlarge ring-1 ring-line-normal",
        className,
      )}
    >
      <div>
        <div className="flex items-center gap-2">
          <h3 className="text-[16px] font-bold leading-6 text-label-normal">
            {DSD_REVIEW_COPY.rail[mode]}
          </h3>
          {count > 0 && (
            <NumberBadge
              max={NUMBER_BADGE_COUNT_MAX}
              className="bg-status-negative text-static-white"
            >
              {count}
            </NumberBadge>
          )}
        </div>
        <p className="mt-1 label-2-regular text-neutral-700">
          {guide.description}
        </p>
      </div>
      <div className="flex flex-col rounded-[12px] bg-blue-500/6 px-4 py-3">
        {collapsible ? (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            className="flex items-center gap-1"
          >
            {header}
            <ChevronDown
              className={cn(
                "ml-auto size-5 text-label-normal transition-transform duration-200",
                open && "rotate-180",
              )}
            />
          </button>
        ) : (
          <div className="flex items-center gap-1">{header}</div>
        )}
        {/* 꺽쇠와 같은 200ms 로 높이도 펼친다 (grid-rows 0fr → 1fr). 접힌 동안은 보조기기에서도 숨김 */}
        <div
          aria-hidden={!open}
          className={cn(
            "grid transition-[grid-template-rows] duration-200",
            open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
          )}
        >
          {/* 위 여백(16px)까지 안쪽에 둬야 접었을 때 높이가 0 이 된다 (min-h-0 은 content-box 만 줄인다) */}
          <div className="min-h-0 overflow-hidden">
            <div className="flex flex-col gap-2 pt-4">
              {guide.steps.map((step, index) => {
                const Icon = icons[index];
                return (
                  <div
                    key={step.title}
                    className="flex items-start gap-2 rounded-[8px] bg-background-base p-2"
                  >
                    <span className="flex size-[34px] shrink-0 items-center justify-center rounded-[6px] bg-blue-500/8 text-primary-color">
                      <Icon className="size-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="label-2-semibold text-label-normal">
                        {step.title}
                      </p>
                      <p className="mt-1 whitespace-pre-line label-2-regular text-neutral-500">
                        {step.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
